import { toLower } from "lodash";
import { useModal } from "react-modal-hook";
import { useUpdatePropertyHiddenFromPublicMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import {
  ButtonSection,
  Container,
  ContentSection,
  HeaderSection,
  PrimaryButtons,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Buttons/Button";
import Modal from "../../Common/Modal";
import { AddressPanelProperty } from "../types";

export interface HidePropertyModalProps {
  onSubmit: () => void;
  onCancel: () => void;
  property: AddressPanelProperty;
}

export const useHidePropertyModal = ({
  onSubmit,
  onCancel,
  property,
}: HidePropertyModalProps) => {
  const [showHidePropertyModal, closeHidePropertyModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          onCancel();
        }}
      >
        <HidePropertyConfirmation
          onCancel={onCancel}
          property={property}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
    [property],
  );

  return [showHidePropertyModal, closeHidePropertyModal] as const;
};

export const HidePropertyConfirmation = ({
  onSubmit,
  onCancel,
  property,
}: HidePropertyModalProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  if (!property) {
    throw Error("Missing Property");
  }

  const hide = property.hiddenFromPublic;

  const hideOrDisplay = hide ? "Display" : "Hide";

  const [updatePropertyHiddenFromPublic, { loading }] =
    useUpdatePropertyHiddenFromPublicMutation({
      onCompleted: data => {
        onSubmit();

        addSuccessToast(
          `The property was successfully ${
            data.updatePropertyHiddenFromPublic?.hiddenFromPublic ?
              "hidden from"
            : "displayed to"
          } public`,
        );
      },
      onError: () => {
        addErrorToast(
          `There was an error ${
            hide ? "hiding" : "displaying"
          } this property. Please try again. If the problem persists, please email support@withforerunner.com`,
        );
      },
    });

  const toggleHidden = async () => {
    if (loading) return;
    track(`Property ${hide ? "displayed" : "hidden"}`, {
      streetAddress: property.streetAddress,
      city: property.city,
    });
    await updatePropertyHiddenFromPublic({
      variables: {
        id: property.id,
        data: { hide: !property.hiddenFromPublic },
      },
    });
  };

  return (
    <Container>
      <HeaderSection>
        <h1>{hideOrDisplay} property</h1>
        <h2>{property.fullAddress}</h2>
      </HeaderSection>
      <ContentSection>
        <p>
          This action will {toLower(hideOrDisplay)} the {property.streetAddress}{" "}
          property and all associated data and files on the public website.{" "}
          {hide ?
            "Members of the public will be able to search for and click on the property on the map."
          : "Members of the public attempting to search or click on the property on the map will find no results."
          }
        </p>
      </ContentSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            size="medium"
            variant="outline"
            onPress={() => onCancel()}
            title="Cancel"
          />
          <Button
            size="medium"
            variant="critical"
            onPress={toggleHidden}
            isPending={loading}
            title={`I understand, ${toLower(hideOrDisplay)} this property`}
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
};
